import { __assign } from "tslib";
import { hasAnyDeliverableItems } from './dispatchTypeLogic';
import moment from 'moment-timezone';
import _ from 'lodash';
import { isAvailableOn } from 'availability';
import { BusinessNotifications } from '../types/BusinessNotifications';
import { DEFAULT_ADDRESS, DEFAULT_ADDRESS_SHORT, NO_ADDRESS, } from '../constants';
import { getAvailableTimingOptions } from './dateTimeLogic';
import { getStatus } from './statusLogic';
export function isSupportsFutureOrder(restaurant) {
    return !restaurant.orders.future.disabled;
}
export function getSupportedDispatchTypes(_a) {
    var deliveryInfos = _a.deliveryInfos, deliveryDisplayableMenu = _a.deliveryDisplayableMenu, getVirtualDispatchTypes = _a.getVirtualDispatchTypes, timezone = _a.timezone, isFutureOrdersOnly = _a.isFutureOrdersOnly, supportsFutureOrder = _a.isSupportsFutureOrder;
    return deliveryInfos.reduce(function (deliveryTypes, deliveryInfo) {
        var _a = getStatus(deliveryInfo.availability, moment().tz(timezone)), status = _a.status, reason = _a.reason;
        var isClosed = status === 'unavailable' &&
            (reason === 'closed' || !supportsFutureOrder);
        var foundDeliverableItems = !deliveryDisplayableMenu ||
            hasAnyDeliverableItems(deliveryDisplayableMenu);
        if (deliveryInfo.type !== 'premises' &&
            !deliveryInfo.inactive &&
            !isClosed &&
            (deliveryInfo.type !== 'delivery' || foundDeliverableItems)) {
            deliveryTypes.add(deliveryInfo.type);
            if (getVirtualDispatchTypes &&
                deliveryInfo.type === 'takeout' &&
                getStatus(deliveryInfo.availability, moment().tz(timezone)).status !==
                    'unavailable' &&
                deliveryInfo.contactlessDineInInfo &&
                deliveryInfo.contactlessDineInInfo.enabled &&
                !isFutureOrdersOnly) {
                deliveryTypes.add('dine-in');
            }
        }
        return deliveryTypes;
    }, new Set());
}
export function getDelayMins(_a) {
    var deliveryInfos = _a.deliveryInfos, dispatchType = _a.dispatchType;
    var areas = _.filter(deliveryInfos, function (info) { return info.type === dispatchType && !info.inactive; });
    var delayMins = _.keys(_.groupBy(_.map(_.map(areas, 'delayMins'), function (delayMin) {
        return delayMin || 0;
    })));
    return _.map(delayMins, function (d) { return parseInt(d, 10); });
}
export function hasNoAvailableDispatchType(restaurant, calendar) {
    return (!isTakeoutAvailable(restaurant, { time: calendar }) &&
        !isDeliveryAvailable(restaurant, { time: calendar }));
}
/**
 * @returns Returns true if takeout is available for the restaurant
 *
 * @note By default this function will check if the restaurant can make a takeout now or in the future
 *
 * # options
 *  - checkForAsap - check if the restaurant can make a takeout now.
 *  - checkForFuture - check if the restaurant can make a takeout in the future.
 *  - time - the time to check for ASAP takeout. default: `moment().tz(restaurant.timezone)`
 */
export function isTakeoutAvailable(restaurant, options) {
    if (options === void 0) { options = {}; }
    var localOptions = __assign({ checkForAsap: true, checkForFuture: true, time: moment().tz(restaurant.timezone) }, options);
    var isAvailableNow = Boolean(restaurant.deliveryInfos.find(function (di) {
        return di.type === 'takeout' &&
            !di.inactive &&
            isAvailableOn(di.availability, localOptions.time);
    }));
    var isAvailableInTheFuture = Boolean(restaurant.deliveryInfos.find(function (di) { return di.type === 'takeout' && !di.inactive; })) && isSupportsFutureOrder(restaurant);
    if (!localOptions.checkForFuture && localOptions.checkForAsap) {
        return isAvailableNow;
    }
    else if (!localOptions.checkForAsap && localOptions.checkForFuture) {
        return isAvailableInTheFuture;
    }
    else if (localOptions.checkForAsap && localOptions.checkForFuture) {
        return isAvailableNow || isAvailableInTheFuture;
    }
    else {
        return false;
    }
}
/**
 * @returns Returns true if delivery is available for the restaurant
 *
 * @note By default this function will check if the restaurant can make a delivery now or in the future
 *
 * # options
 *  - checkForAsap - check if the restaurant can make a delivery now.
 *  - checkForFuture - check if the restaurant can make a delivery in the future.
 *  - time - the time to check for ASAP takeout. default: `moment().tz(restaurant.timezone)`
 */
export function isDeliveryAvailable(restaurant, options) {
    if (options === void 0) { options = {}; }
    var localOptions = __assign({ checkForAsap: true, checkForFuture: true, time: moment().tz(restaurant.timezone) }, options);
    var isAvailableNow = Boolean(restaurant.deliveryInfos.find(function (di) {
        return di.type === 'delivery' &&
            isAvailableOn(di.availability, localOptions.time);
    }));
    var isAvailableInTheFuture = Boolean(restaurant.deliveryInfos.find(function (di) {
        var _a = getStatus(di.availability, localOptions.time), status = _a.status, reason = _a.reason;
        var isClosed = status === 'unavailable' && reason === 'closed';
        return di.type === 'delivery' && !isClosed;
    })) && isSupportsFutureOrder(restaurant);
    if (!localOptions.checkForFuture && localOptions.checkForAsap) {
        return isAvailableNow;
    }
    else if (!localOptions.checkForAsap && localOptions.checkForFuture) {
        return isAvailableInTheFuture;
    }
    else if (localOptions.checkForAsap && localOptions.checkForFuture) {
        return isAvailableNow || isAvailableInTheFuture;
    }
    else {
        return false;
    }
}
export function getAllBusinessNotifications(restaurant, hasAnyPaymentMethods, dispatchTime) {
    var calendar = moment(dispatchTime).tz(restaurant.timezone);
    var restStatus = getStatus(restaurant.openTimes, calendar);
    var notifications = [];
    if (!hasAnyPaymentMethods) {
        notifications.push({
            notification: { notification: BusinessNotifications.NoPaymentMethods },
            weight: 1100,
        });
    }
    if (!isPremiumRestaurant(restaurant)) {
        notifications.push({
            notification: { notification: BusinessNotifications.NotPremium },
            weight: 1000,
        });
    }
    if (isNoPhysicalLocation(restaurant.address)) {
        notifications.push({
            notification: { notification: BusinessNotifications.NoPhysicalLocation },
            weight: 900,
        });
    }
    if (restStatus.status === 'unavailable') {
        var hasAvailableDateAndTimes = getAvailableTimingOptions(restaurant, 'delivery').includes('later') ||
            getAvailableTimingOptions(restaurant, 'takeout').includes('later');
        var closedNotifications = isSupportsFutureOrder(restaurant) && hasAvailableDateAndTimes
            ? BusinessNotifications.OnlyFutureOrders
            : BusinessNotifications.RestaurantClosed;
        notifications.push({
            notification: { notification: closedNotifications },
            weight: 800,
        });
    }
    var oloStatus = getStatus(_.get(restaurant, 'orders.availability'), calendar);
    if (oloStatus.status === 'unavailable' ||
        hasNoAvailableDispatchType(restaurant, calendar)) {
        var locale = restaurant.locale;
        var comment = _.get(oloStatus, "comment[" + locale + "]");
        notifications.push({
            notification: {
                notification: BusinessNotifications.OlOunavailable,
                comment: comment,
            },
            weight: 600,
        });
    }
    notifications.sort(function (a, b) { return b.weight - a.weight; });
    return notifications.map(function (nd) { return nd.notification; });
}
function isPremiumRestaurant(restaurant) {
    return _.find(restaurant.products, {
        id: 'com.wix.restaurants.orders',
        param: 'premium',
    });
}
export var isNoPhysicalLocation = function (address) {
    return (address &&
        (address.formatted === NO_ADDRESS ||
            address.formatted === DEFAULT_ADDRESS ||
            address.formatted === DEFAULT_ADDRESS_SHORT));
};
export var getIsCurbsideEnabled = function (deliveryInfos) {
    return deliveryInfos.some(function (deliveryInfo) { var _a; return deliveryInfo.type === 'takeout' && ((_a = deliveryInfo.curbsideInfo) === null || _a === void 0 ? void 0 : _a.enabled); });
};
export var getCurbsideInfo = function (deliveryInfos) {
    var _a;
    var dispatchInfo = deliveryInfos.find(function (deliveryInfo) { return deliveryInfo.type === 'takeout'; });
    var takeoutDispatchInfo = dispatchInfo;
    return takeoutDispatchInfo && ((_a = takeoutDispatchInfo.curbsideInfo) === null || _a === void 0 ? void 0 : _a.enabled)
        ? takeoutDispatchInfo.curbsideInfo
        : undefined;
};
export var doesOrderHasCurbside = function (order) {
    return !!_.get(order, 'delivery.isCurbside');
};
export function getNumberOfDaysAheadAvailableForOrders(ordersInfo) {
    var _a;
    var maxDelayMinutess = (_a = ordersInfo.future.delayMins) === null || _a === void 0 ? void 0 : _a.max;
    if (typeof maxDelayMinutess === 'number') {
        return maxDelayMinutess / 1440;
    }
    else {
        return 7;
    }
}
//# sourceMappingURL=restaurantLogic.js.map